import { forwardRef } from "react";
import styled from "@emotion/styled";

import EllipsisText from "components/ui/tooltip/EllipsisText";
import UIcon from "lib/icons/untitled_ui/UIcon";
import type { GroupType, Option, OptionValue } from "./types";
import { flattenItems } from "./utils";

export type ComboboxItemProps = {
  option: Option;
  value: OptionValue[];
  selectItem: (item: Option) => void;
  multiple: boolean;
  highlightedIndex: number;
  filteredItems: Option[] | GroupType[];
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

export const ComboboxItem = forwardRef<HTMLDivElement, ComboboxItemProps>(
  (
    { option, value, selectItem, multiple, highlightedIndex, filteredItems, children, ...rest },
    ref,
  ) => {
    const isSelected = value.includes(option.value);
    const allOptions = flattenItems(filteredItems);
    const itemIndex = allOptions.findIndex((opt) => opt.value === option.value);
    const isHighlighted = itemIndex === highlightedIndex;

    return (
      <ItemContainer
        {...rest}
        ref={ref}
        onClick={() => selectItem(option)}
        data-highlighted={isHighlighted}
        data-multiple={multiple}
        role="option"
        aria-label={option.label}
        aria-selected={isSelected}
      >
        {multiple && (
          <Checkbox role="checkbox" aria-checked={isSelected}>
            {isSelected && <UIcon icon="checkOutlined" size={14} color="sysBackgroundDefault" />}
          </Checkbox>
        )}
        {children ? (
          <ItemContentWrapper>{children}</ItemContentWrapper>
        ) : (
          <ItemContent>{option.label}</ItemContent>
        )}
        {!multiple && isSelected && (
          <CheckIcon>
            <UIcon icon="checkOutlined" size={16} color="sysPrimaryDefault" />
          </CheckIcon>
        )}
      </ItemContainer>
    );
  },
);
ComboboxItem.displayName = "ComboboxItem";

const ItemContainer = styled.div(({ theme }) => ({
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  fontSize: 14,
  color: theme.colors.sysTextBlack,
  backgroundColor: "transparent",
  transition: "background-color 0.15s",

  /* Highlight on hover */
  '&[data-highlighted="true"]': {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },

  /* For single select, highlight selected items */
  '&[data-multiple="false"][aria-selected="true"]': {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },

  "&:hover": {
    backgroundColor: theme.colors.sysTextDefault + "0A", // 4% opacity
  },
}));

const ItemContent = styled(EllipsisText)({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const ItemContentWrapper = styled.div({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
});

const Checkbox = styled.div(({ theme }) => ({
  width: 16,
  height: 16,
  borderRadius: 3,
  border: `1.5px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  transition: "all 0.15s",

  '&[aria-checked="true"]': {
    border: `1.5px solid ${theme.colors.sysPrimaryDefault}`,
    backgroundColor: theme.colors.sysPrimaryDefault,
  },
}));

const CheckIcon = styled.div({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});
